/// <amd-module name="Test/TestResultsGrid/Sources/TestResultsGridSource" />
import * as Common from "Test/common-util/Common";
import { TestResultsGroupPivots } from "Test/common-util/Common";
import * as TCMContracts from "Test/common-util/Contracts/Test";
import * as TestManagementService from "Test/Client/TestManagementServices";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { TestCaseResultsWithContinuationToken as ITestCaseResultsWithContinuationToken } from "Test/common-util/Contracts/ExtendedTest";
import { Constants, FilterSettings } from "Test/TestResultsGrid/TestResultGridHelper";
import { IVssPageContext } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { ViewContext } from "Test/common-util/Common";
import * as Telemetry from "Test/common-util/Telemetry";
import * as TelemetryConstants from "Test/common-util/TelemetryConstants";
import { ShallowTestCaseResultData } from "../Actions/TestResultsGridActionHub";
import { IVssPerformanceService } from "VSS/Platform/Performance";
import * as VS_Services from "VSS/Platform/Contracts/WebApi";

export class TestResultGridSource {
    public constructor(private pageContext: IVssPageContext) {
        this._continuationTokenForTestResults = undefined;
        this._groupByFieldCache = {};
        this._actionCache = {};
        this._resultsFetchedForCurrentBatch = 0;
        this._fetchingResults = false;
    }

    public resetCache() {
        this._continuationTokenForTestResults = undefined;
        this._groupByFieldCache = {};
        this._actionCache = {};
        this._detailedTestResultCache = {};
        this._resultsFetchedForCurrentBatch = 0;
        this._fetchingResults = false;
    }

    public getTestTagSummary(artifact: Common.IViewContextData): Promise<TCMContracts.TestTagSummary> {
        let currentArtifact = artifact.data.mainData;
        let tcmService = this.pageContext.getService<TestManagementService.ITestManagementService>(TestManagementService.TestManagementServiceName);
        if (artifact.viewContext === Common.ViewContext.Build) {
            return tcmService.getTestTagSummaryForBuild(currentArtifact.id);
        } else {
            let filters = (artifact.data.subData) ? artifact.data.subData : null,
                selectedEnvironmentId = (filters && filters.environment) ? filters.environment.id : null;

            return tcmService.getTestTagSummaryForRelease(currentArtifact.id, selectedEnvironmentId);
        }
    }

    public getTestResultsByGroup(artifact: Common.IViewContextData, groupByField: TestResultsGroupPivots, filterString?: string, isInProgress?: boolean): Promise<TCMContracts.TestResultsDetails | null> {
        if (!this._shouldFetchGroupedResults(groupByField) ||
            this._groupByFieldCache[groupByField]) {
            return new Promise((resolve, reject) => {
                const groupedResults: TCMContracts.TestResultsDetails = {
                    groupByField: groupByField,
                    resultsForGroup: []
                };
                resolve(groupedResults);
            });
        }

        let currentArtifact = artifact.data.mainData;
        let tcmService = this.pageContext.getService<TestManagementService.ITestManagementService>(TestManagementService.TestManagementServiceName);
        filterString = filterString ? filterString : "";
        if (artifact.viewContext === Common.ViewContext.Build) {
            return tcmService.getTestResultsByGroupForBuild(currentArtifact.id, Common.SourceWorkflow.BUILD_SOURCE_WORKFLOW, groupByField as string, filterString, "", true, false);
        } else {
            let filters = (artifact.data.subData) ? artifact.data.subData : null,
                selectedEnvironmentId = (filters && filters.environment) ? filters.environment.id : null;

            return tcmService.getTestResultsByGroupForRelease(currentArtifact.id, selectedEnvironmentId, Common.SourceWorkflow.RELEASE_SOURCE_WORKFLOW, groupByField, filterString, "", true, false);
        }
    }

    public getTestResultsByGroupFromDataProvider(artifact: Common.IViewContextData): Promise<TCMContracts.TestResultsDetails | undefined> {
        return new Promise<TCMContracts.TestResultsDetails | undefined>(async (resolve, reject) => {
            const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
            try {
                if (artifact.status === Common.ViewContextStatus.InProgress) {
                    const testResultDetails = await this._getTestResultsByGroupFromDataProviderAsync(artifact, contributionService, this._inProgressStateDPRequestParam);
                    return resolve(testResultDetails);
                }

                let performanceService = this.pageContext.getService<IVssPerformanceService>("IVssPerformanceService");
                let resultDetailsForBuild;

                // we are doing this hack here because Summary tab is loading all the data provider of testTab which causing stale data issue for grid data provider.
                // BUG: https://dev.azure.com/mseng/AzureDevOps/_workitems/edit/1674136 
                if(performanceService.isFastPageSwitchScenario()) {
                    resultDetailsForBuild = contributionService.getData(this._getDataProviderName(artifact), this._completedStateDPRequestParam);
                }
                else {
                    resultDetailsForBuild = contributionService.getData(this._getDataProviderName(artifact));
                }

                if (this._isDataProviderCacheValid(contributionService, resultDetailsForBuild, artifact)) {
                    return resolve((resultDetailsForBuild as { testResultsDetails: TCMContracts.TestResultsDetails, buildId: number }).testResultsDetails);
                }

                Telemetry.publishEvent(this.pageContext, artifact, TelemetryConstants.featureTestTab_ErrorInResultsGridDataProvider, { "DataProviderName": this._getDataProviderName(artifact), "error": `Dataprovider data is invalid or stale` });
                return resolve(await this._refreshAndGetDataProviderCache(contributionService, artifact));
            } catch (error) {
                reject(error);
            }
        });
    }

    private _isDataProviderCacheValid(contributionService: IVssContributionService, reports: unknown | undefined, artifact: Common.IViewContextData): boolean {
        if (!reports) {
            return false;
        }

        if (artifact.viewContext === Common.ViewContext.Build) {
            const buildReport = reports as { testResultsDetails: TCMContracts.TestResultsDetails, buildId: number };
            if (buildReport.buildId !== artifact.data.mainData.id) {
                return false;
            }
        }

        if (artifact.viewContext === Common.ViewContext.Release) {
            const releaseReport = reports as { testResultsDetails: TCMContracts.TestResultsDetails, releaseId: number, releaseEnvId: number };
            if (releaseReport.releaseId !== artifact.data.mainData.id || releaseReport.releaseEnvId !== artifact.data.subData.environment.id) {
                return false;
            }
        }

        // Making data provider cache invalid when you have inprogress data provider cache in completed state.
        return contributionService.getData(this._getDataProviderName(artifact), this._inProgressStateDPRequestParam) ? false : true;
    }

    private async _refreshAndGetDataProviderCache(contributionService: IVssContributionService, artifact: Common.IViewContextData): Promise<TCMContracts.TestResultsDetails> {
        const refreshedTestResultDetails = contributionService.getData(this._getDataProviderName(artifact), this._completedStateDPRequestParam);

        // Not refreshing the summary if it's already refreshed in completed state.
        const testSummary = this._isDataProviderCacheValid(contributionService, refreshedTestResultDetails, artifact) ? refreshedTestResultDetails as TCMContracts.TestResultsDetails : await contributionService.getDataAsync(this._getDataProviderName(artifact), this._completedStateDPRequestParam, true) as TCMContracts.TestResultsDetails;
        return testSummary;
    }

    private _getTestResultsByGroupFromDataProviderAsync(artifact: Common.IViewContextData, contributionService: IVssContributionService, requestParams: { [key: string]: any }): Promise<TCMContracts.TestResultsDetails | undefined> {
        return new Promise<TCMContracts.TestResultsDetails | undefined>((resolve, reject) => {
            contributionService.getDataAsync(this._getDataProviderName(artifact), requestParams, true).then((value: { testResultsDetails: TCMContracts.TestResultsDetails, buildId: number }) => {
                resolve(value.testResultsDetails);
            })
                .catch((reason: any) => {
                    reject(reason);
                });
        });
    }

    private _getDataProviderName(artifact: Common.IViewContextData): string {
        if (artifact.viewContext === ViewContext.Build) {
            return this.buildResultGridDataProviderName;
        }
        return this.releaseResultGridDataProviderName;
    }

    public getTestResultsByQueryForGivenResultIdentifiers(results: Common.TestCaseResultIdentifier[]): Promise<TCMContracts.TestResultsQuery> {
        const resultsQuery: TCMContracts.TestCaseResult[] = [];

        results.map(resultIdentifier => {
            resultsQuery.push(<TCMContracts.TestCaseResult>{ id: resultIdentifier.testResultId, testRun: <TCMContracts.ShallowReference>{ id: resultIdentifier.testRunId.toString() } });
        });

        return this._getTestResultsByQuery(resultsQuery);
    }

    public getSelectedTestCaseResult(artifact: Common.IViewContextData, runId: number, resultId: number): Promise<TCMContracts.TestCaseResult> {
        let tcmService = this.pageContext.getService<TestManagementService.ITestManagementService>(TestManagementService.TestManagementServiceName);
        let includeDetails: TCMContracts.ResultDetails = TCMContracts.ResultDetails.SubResults;

        return tcmService.getResultById(runId, resultId, (includeDetails) ? includeDetails : TCMContracts.ResultDetails.None);
    }

    private _getTestResultsByQuery(results: TCMContracts.TestCaseResult[]): Promise<TCMContracts.TestResultsQuery> {
        let tcmService = this.pageContext.getService<TestManagementService.ITestManagementService>(TestManagementService.TestManagementServiceName);

        const fields = [
            "Outcome",
            "TestCaseTitle",
            "AutomatedTestName",
            "AutomatedTestStorage",
            "TestResultGroupType"
        ];

        fields.push("Duration");
        fields.push("ReleaseEnvId");
        fields.push("Owner");
        fields.push("FailingSince");
        fields.push("DateStarted");
        fields.push("DateCompleted");

        if (isFeatureFlagEnabled(this.pageContext, Common.FeatureFlagConstants.TriReportCustomization, false)) {
            fields.push(Constants.OutcomeConfidenceField);
        }

        if (isFeatureFlagEnabled(this.pageContext, Common.FeatureFlagConstants.EnableTestFlakiness, false)) {
            fields.push(Constants.IsTestResultFlakyField);
            fields.push(Constants.TestResultFlakyStateField);
        }

        results = results.filter((currentResult, currentIndex, resultsList) => {
            return resultsList.indexOf(currentResult) === currentIndex;
        });

        const query: TCMContracts.TestResultsQuery = { results: results, fields: fields } as TCMContracts.TestResultsQuery;
        return tcmService.getTestResultsByQuery(query);
    }

    public getShallowTestResultDetails(artifact: Common.IViewContextData, action: string, autoFetchResults: boolean = false, outcomes?: TCMContracts.TestOutcome[]): Promise<ShallowTestCaseResultData> {
        let outComeValues: string | undefined = undefined;
        outComeValues = outcomes?.map(val => val.toString()).join(",");
        let tcmService = this.pageContext.getService<TestManagementService.ITestManagementService>(TestManagementService.TestManagementServiceName);
        if (!this._shouldFetchShallowResults(action, autoFetchResults)) {
            return new Promise<ShallowTestCaseResultData>((resolve, reject) => {
                const batchSize: number = autoFetchResults ? FilterSettings.getMegaResultsSize(this.pageContext) : Constants.batchResultsSize;
                if (this._resultsFetchedForCurrentBatch === batchSize) {
                    this._resultsFetchedForCurrentBatch = 0;
                }
                const results: TCMContracts.ShallowTestCaseResult[] = [];

                // Fill the source of results.
                resolve({ shallowResults: results, isResultFetchedFromServer: false });
            });
        }

        // to prevent multiple calls on Load More Click
        this._fetchingResults = true;
        this._actionCache[action] = true;
        let continuationToken: string | undefined = this._continuationTokenForTestResults;
        if(artifact.runId){
            continuationToken = this._continuationTokenForTestResults !== undefined? this._continuationTokenForTestResults: artifact.runId + "_0";
            if (this._continuationTokenForTestResults){
                const runId = this._continuationTokenForTestResults.split("_")[0];
                if(+runId !== +artifact.runId){
                    return new Promise<ShallowTestCaseResultData>((resolve, reject) => {
                        const results: TCMContracts.ShallowTestCaseResult[] = [];
                        this._fetchingResults = false;
                        resolve({ shallowResults: results, isResultFetchedFromServer: true });
                    });
                }
            }
        }
        let currentArtifact = artifact.data.mainData;
        this._artifactId = currentArtifact.id;
        return new Promise<ShallowTestCaseResultData>((resolve, reject) => {
            let batchSize: number = isFeatureFlagEnabled(this.pageContext, Common.FeatureFlagConstants.EnableFetchAllResultsUntilMegaBatchSize, false) ? FilterSettings.getNewShallowResultsBatchSize(this.pageContext) : Constants.shallowResultsBatchSize;
            if (artifact.viewContext === Common.ViewContext.Build) {
                if(this._shouldUsePipelineAPi(artifact)){
                    tcmService.getTestResultsByPipeline(currentArtifact.id, undefined, undefined, undefined, outcomes, batchSize, continuationToken)
                    .then((testCaseResultsWithContinuationToken: VS_Services.PagedList<TCMContracts.ShallowTestCaseResult>) => {
                        if (currentArtifact.id === this._artifactId) {
                            this._continuationTokenForTestResults = testCaseResultsWithContinuationToken.continuationToken != null? testCaseResultsWithContinuationToken.continuationToken : undefined;
                            this._resultsFetchedForCurrentBatch += testCaseResultsWithContinuationToken.length;
                            this._fetchingResults = false;

                            // Fill the source of results.
                            resolve({ shallowResults: testCaseResultsWithContinuationToken, isResultFetchedFromServer: true });
                        }
                    },
                        (error: any) => {
                            if (currentArtifact.id === this._artifactId) {
                                reject(error);
                            }
                    });
                }
                else {
                    tcmService.getTestResultsByBuildWithContinuationToken(currentArtifact.id, Common.SourceWorkflow.BUILD_SOURCE_WORKFLOW, outComeValues, batchSize, continuationToken)
                        .then((testCaseResultsWithContinuationToken: ITestCaseResultsWithContinuationToken) => {
                            if (currentArtifact.id === this._artifactId) {
                                this._continuationTokenForTestResults = testCaseResultsWithContinuationToken.continuationToken;
                                this._resultsFetchedForCurrentBatch += testCaseResultsWithContinuationToken.results.length;
                                this._fetchingResults = false;

                                // Fill the source of results.
                                resolve({ shallowResults: testCaseResultsWithContinuationToken.results, isResultFetchedFromServer: true });
                            }
                        },
                            (error: any) => {
                                if (currentArtifact.id === this._artifactId) {
                                    reject(error);
                                }
                        });
                }
            } else {
                let filters = (artifact.data.subData) ? artifact.data.subData : null,
                    selectedEnvironmentId = (filters && filters.environment) ? filters.environment.id : null;

                tcmService.getTestResultsByReleaseWithContinuationToken(currentArtifact.id, selectedEnvironmentId, Common.SourceWorkflow.RELEASE_SOURCE_WORKFLOW, outComeValues, batchSize, continuationToken)
                    .then((testCaseResultsWithContinuationToken: ITestCaseResultsWithContinuationToken) => {
                        if (currentArtifact.id === this._artifactId) {
                            this._continuationTokenForTestResults = testCaseResultsWithContinuationToken.continuationToken;
                            this._resultsFetchedForCurrentBatch += testCaseResultsWithContinuationToken.results.length;
                            this._fetchingResults = false;

                            // Fill the source of results.
                            resolve({ shallowResults: testCaseResultsWithContinuationToken.results, isResultFetchedFromServer: true });
                        }
                    },
                        (error: any) => {
                            if (currentArtifact.id === this._artifactId) {
                                reject(error);
                            }
                        });
            }
        });
    }

    private _shouldFetchShallowResults(action: string, autoFetchResults: boolean) {
        if (this._fetchingResults) {
            return false;
        }

        // For filter/groupBy changed only first time results should be fetched
        if (action === Constants.filterchangedAction
            || action === Constants.groupByChangedAction) {
            if (this._actionCache[Constants.filterchangedAction] ||
                this._actionCache[Constants.groupByChangedAction]) {
                return false;
            }
        }

        // If results fetched for current batch are there and continuation token null, then donot fetch
        // If all results for current batch has been fetched, donot fetch more
        const batchSize: number = autoFetchResults ? FilterSettings.getMegaResultsSize(this.pageContext) : Constants.batchResultsSize;
        if ((this._resultsFetchedForCurrentBatch > 0
            && !this._continuationTokenForTestResults)
            || this._resultsFetchedForCurrentBatch === batchSize) {
            return false;
        } else {
            return true;
        }
    }

    // Fetch grouped results only in Group By TestRun, Requirement, TestSuite
    private _shouldFetchGroupedResults(groupByField: TestResultsGroupPivots) {
        return (groupByField === TestResultsGroupPivots.Group_By_Test_Run
            || groupByField === TestResultsGroupPivots.Group_By_Requirement
            || groupByField === TestResultsGroupPivots.Group_By_Test_Suite);
    }

    private _shouldUsePipelineAPi(artifact: Common.IViewContextData): boolean {
        if(artifact.contributionSource === Common.ContributionSource.Pipeline){
            return isFeatureFlagEnabled(this.pageContext, this._queryTcmDirectly, false) && isFeatureFlagEnabled(this.pageContext, this._metricsAPIInUnifiedPipeline, false);
        }
        else {
            return false;
        }
    }

    private _detailedTestResultCache = {};
    private _continuationTokenForTestResults: string | undefined;
    private _groupByFieldCache: { [key: string]: boolean };
    private _actionCache: { [key: string]: boolean };
    private _resultsFetchedForCurrentBatch: number;
    private _fetchingResults: boolean;
    private _artifactId: number;
    private readonly _inProgressStateDPRequestParam = { "state": "InProgress" };
    private readonly _completedStateDPRequestParam = { "state": "Completed" };
    private readonly buildResultGridDataProviderName = "ms.vss-test-web.test-tab-build-resultdetails-data-provider";
    private readonly releaseResultGridDataProviderName = "ms.vss-test-web.test-tab-release-resultdetails-data-provider";
    private readonly _metricsAPIInUnifiedPipeline: string = "WebAccess.TestManagement.UnifiedPipelineMetricsAPI";
    private readonly _queryTcmDirectly = "WebAccess.TestManagement.QueryFromTcmService";
}
